import ProfileItemForm from "./ProfileItemForm";
import SearchBrandsInput from "../../../components/form/SearchBrandsInput";
import React, { useMemo, useState } from "react";
import { Button, Divider, Empty, Select, Tag, Input } from "antd";
import FlipMove from "react-flip-move";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { PictureOutlined } from "@ant-design/icons";
import InfoTooltip from "../../../components/InfoTooltip";
import { useAPI } from "../../../config/API";
import ProfileKeyClientRow from "../../profiles/components/ProfileKeyClientRow";
import { FormInputComponent } from "../../../components/form/Form";
import { TalentAppCard } from './TalentAppCard';

const MAX_TAGS_COUNT = 12
const MAX_BRANDS_COUNT = 4
const MAX_LINKS_COUNT = 3
const MAX_IMAGE_ASSETS_COUNT = 10
const MAX_DOC_ASSETS_COUNT = 10

const MIN_SUMMARY_LENGTH = 50

/**
 * The optional (collapsible) sections of the work sample form, in display order.
 * Everything not listed here is a required "basic" and is always visible.
 */
const OPTIONAL_SECTION_KEYS = ['visuals', 'brands', 'tags', 'attachments']

const tagRender = (props) => {
    let { label, value, closable, onClose, type, className } = props || {};
    const colorMap = {
        industries: 'default',
        skills: 'geekblue',
        disciplines: 'purple'
    };

    const onPreventMouseDown = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Tag
            color={colorMap[type] || 'default'}
            onMouseDown={onPreventMouseDown}
            closable={closable}
            onClose={onClose}
            className={className}>
            {label}
        </Tag>
    );
};

const isValidURLFormat = (string) => {
    const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w- .\/?%&=]*)?$/;
    return urlPattern.test(string);
}

/**
 * Strip html tags so we can measure what the talent actually typed
 */
const plainTextLength = (html) => {
    return String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .length;
}

/**
 * Small status pill used on section headers
 */
const StatusPill = ({ complete, children, className = '' }) => (
    <span className={TB.mapClass(
        "flex flex-row items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xxs font-semibold whitespace-nowrap border " + className,
        {
            "bg-white border-wrippleSuccessDarker text-wrippleSuccessDarker": complete,
            "bg-light-gray3 border-light-gray2 text-light-gray4": !complete,
        }
    )}>
        {complete && <i className="fa fa-check text-xxs" />}
        <span>{children}</span>
    </span>
);

/**
 * Clickable header for an optional, collapsible section
 */
const CollapsibleSectionHeader = ({
    sectionKey,
    title,
    hint,
    preview,
    icon,
    isOpen,
    count,
    countLabel,
    canToggle,
    onToggle
}) => {

    const hasContent = count > 0;
    const subtitle = (!isOpen && hasContent && preview) ? preview : hint;

    return (
        <div className="w-full px-3.5 pt-4">
            <div
                id={`work-sample-section-${sectionKey}`}
                role="button"
                aria-expanded={isOpen}
                aria-controls={`work-sample-section-body-${sectionKey}`}
                tabIndex={canToggle ? 0 : -1}
                onKeyDown={(event) => {
                    if (!canToggle) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onToggle();
                    }
                }}
                onClick={() => canToggle && onToggle()}
                className={TB.mapClass(
                    "w-full flex flex-row items-center gap-3 px-3 py-2.5 rounded-lg border",
                    {
                        "clickable hover:border-primary-lightest": canToggle,
                        "bg-light-gray3 border-primary-lightest": isOpen,
                        "bg-white border-light-gray2": !isOpen,
                    }
                )}
                style={{ transition: 'background-color 150ms ease, border-color 150ms ease' }}>

                {canToggle && (
                    <i className="fa fa-chevron-right text-xxs text-light-gray4"
                        style={{
                            transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 150ms ease'
                        }} />
                )}

                <i className={TB.mapClass(icon + " text-sm w-4 text-center", {
                    "text-primary": hasContent,
                    "text-light-gray4": !hasContent,
                })} />

                <div className="flex flex-col items-start text-left min-w-0">
                    <span className="text-sm font-semibold text-dark-gray leading-tight">
                        {title}
                    </span>
                    {subtitle && (
                        <span className="text-xxs text-light-gray4 leading-tight mt-0.5 truncate max-w-full">
                            {subtitle}
                        </span>
                    )}
                </div>

                <div className="ml-auto flex flex-row items-center gap-2">
                    <StatusPill complete={hasContent}>
                        {hasContent ? `${count} ${countLabel}` : 'Optional'}
                    </StatusPill>
                    {canToggle && (
                        <span className="text-xxs text-primary font-semibold whitespace-nowrap hidden xs:inline">
                            {isOpen ? 'Hide' : (hasContent ? 'Edit' : 'Add')}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default class KeyClientsForm extends ProfileItemForm {

    static ItemType = 'Work Sample'

    static FormClass = 'talent-role-form add-talent-form add-client-form '

    static shouldUseTalentAppCard = true;
    static ShowProfilePreview = true;

    constructor(props) {
        super(props);

        this.state = {
            ...this.state,
            useCustomClientInput: !this.state.model?.brand && this.state.model?.id,
            searchInput: '',
            // Sections that already hold content start expanded so nothing looks lost
            openSections: this.initialOpenSections(this.state.model)
        }
    }

    getModelFromProps() {

        let {
            industries = [], skills = [], disciplines = [], assets, docAssets = [], imgAssets, ...model
        } = { ...(this.props.model || {}) }

        imgAssets = TB.map(imgAssets || [], asset => {
            if (asset.id) {
                return {
                    ...asset,
                    thumbUrl: asset.path,
                    url: asset.path,
                }
            }
            return asset
        })
        industries = TB.map(industries, item => ({ ...item, type: 'industries', value: `industry-${parseInt(item.id)}` }))
        skills = TB.map(skills, item => ({ ...item, type: 'skills', value: `skill-${parseInt(item.id)}` }))
        disciplines = TB.map(disciplines, item => ({
            ...item,
            type: 'disciplines',
            value: `discipline-${parseInt(item.id)}`
        }))


        return { ...(this.props.model || {}), imgAssets, industries, skills, disciplines }
    }

    /**
     * getModelFromProps() always hands back the four derived collections, so the
     * base class key count can never reach zero. Ignore them unless they hold
     * something, otherwise a brand new work sample never looks new and the empty
     * placeholder is left behind when the talent cancels.
     *
     * @returns boolean
     */
    creatingNewItem() {
        let {
            imgAssets = [],
            industries = [],
            skills = [],
            disciplines = [],
            ...model
        } = this.getModelFromProps();

        if (TB.keys(model).length) {
            return false;
        }

        return !imgAssets.length
            && !industries.length
            && !skills.length
            && !disciplines.length;
    }

    /**
     * A work sample takes a while to fill in, so confirm before throwing away a
     * new one. Cancelling still removes it from the list rather than leaving an
     * empty row behind.
     *
     * @returns void
     */
    async onCancel() {

        if (!this.creatingNewItem()) {
            return super.onCancel();
        }

        if (this.hasChanged() && !(await this.confirmDiscardChanges())) {
            return;
        }

        // Submitting an empty model removes the placeholder from the list
        await this.props.onSubmit();

        return this.props.onEditing(false);
    }

    onChange(props) {

        let changedModel = TB.cloneDeep(this.state.model);
        for (let [key, value] of Object.entries(props)) {
            changedModel = TB.set(changedModel, key, value);
        }

        if (!this.state.model?.brand && props.brand) {
            changedModel.headline = props.brand.name;
            changedModel.logo_path = props.brand.icon;
        }

        if (changedModel.imgAssets?.length || changedModel.docAssets?.length) {
            changedModel.assets = TB.concat(TB.cloneDeep(changedModel.imgAssets), TB.cloneDeep(changedModel.docAssets));
        } else changedModel.assets = []

        return super.onChange(changedModel);
    }

    /*
    |--------------------------------------------------------------------------
    | Collapsible section state
    |--------------------------------------------------------------------------
    */

    /**
     * How much content each optional section currently holds
     *
     * @returns {Object}
     */
    sectionCounts(model = this.state.model) {
        let {
            industries = [], skills = [], disciplines = [], brands = [],
            imgAssets = [], docAssets = [], links = []
        } = model || {};

        return {
            visuals: (model?.image_path ? 1 : 0) + (imgAssets?.length || 0),
            brands: brands?.length || 0,
            tags: TB.concat(disciplines, skills, industries)?.length || 0,
            attachments: (docAssets?.length || 0) + (links?.length || 0),
        }
    }

    /**
     * Sections holding content open by default, empty ones stay tucked away
     *
     * @returns {Object}
     */
    initialOpenSections(model) {
        let counts = this.sectionCounts(model);

        return TB.reduce(OPTIONAL_SECTION_KEYS, (open, key) => {
            open[key] = counts[key] > 0;
            return open;
        }, {});
    }

    /**
     * Sections can only be toggled while the form is being edited
     */
    canToggleSections() {
        return !!this.props.isEditing && !this.props.disabled && !this.props.isDisabled;
    }

    isSectionOpen(key) {
        // Read only view: show whatever has content, hide the rest entirely
        if (!this.props.isEditing) {
            return this.sectionCounts()[key] > 0;
        }
        return !!this.state.openSections?.[key];
    }

    toggleSection(key) {
        this.setState(state => ({
            openSections: {
                ...(state.openSections || {}),
                [key]: !state.openSections?.[key]
            }
        }));
    }

    setAllSections(isOpen) {
        this.setState({
            openSections: TB.reduce(OPTIONAL_SECTION_KEYS, (open, key) => {
                open[key] = isOpen;
                return open;
            }, {})
        });
    }

    allSectionsOpen() {
        return TB.every(OPTIONAL_SECTION_KEYS, key => this.isSectionOpen(key));
    }

    /**
     * Progress of the three fields we actually require
     *
     * @returns {Object}
     */
    requiredProgress() {
        let model = this.state.model || {};

        let checks = [
            !!TB.trim(String(model.headline || '')),
            !!model.year,
            plainTextLength(model.description) >= MIN_SUMMARY_LENGTH,
        ];

        let completed = TB.filter(checks, Boolean).length;

        return {
            completed,
            total: checks.length,
            isComplete: completed === checks.length
        }
    }

    /**
     * Build one collapsible optional section
     *
     * @returns {Object} a form field section
     */
    collapsibleSection({ key, title, hint, preview, icon, count, countLabel, fields }) {

        const isOpen = this.isSectionOpen(key);
        const canToggle = this.canToggleSections();
        // In read only mode an empty section is removed from the form altogether
        const isSectionHidden = !this.props.isEditing && count < 1;

        return {
            style: styles.collapsibleSection,
            groupStyle: { width: '100%' },
            fields: {
                [`${key}_header`]: {
                    hideVisibility: isSectionHidden,
                    columns: 12,
                    className: 'w-full',
                    component: (
                        <CollapsibleSectionHeader
                            sectionKey={key}
                            title={title}
                            hint={hint}
                            preview={preview}
                            icon={icon}
                            isOpen={isOpen}
                            count={count}
                            countLabel={countLabel}
                            canToggle={canToggle}
                            onToggle={() => this.toggleSection(key)} />
                    )
                },
                [`${key}_body`]: {
                    hideVisibility: isSectionHidden,
                    className: 'field-section work-sample-section-body',
                    style: (isOpen && !isSectionHidden) ? styles.sectionBodyOpen : styles.sectionBodyClosed,
                    fields
                }
            }
        }
    }

    /**
     * The fields mapping for the form
     */
    formFields_LEGACY = () => {

        // 'key_clients.*.industries'     => 'required|integer|exists:client_industries',
        // 'key_clients.*.name'            => 'required|string|max:255',
        // 'key_clients.*.year'            => 'required|integer|max:'.now()->year.'|min:1950',
        // 'key_clients.*.image_path'      => 'nullable|string|max:255' OR 'nullable|image',
        // 'key_clients.*.logo_path'       => 'nullable|string|max:255' OR 'nullable|image',
        // 'key_clients.*.description'     => 'nullable|string|max:255',
        let {
            industries = [],
            skills = [],
            disciplines = [],
            brands = [],
            imgAssets = [],
            docAssets = [],
            links = [],
            ...props
        } = this.state.model;

        const allTags = TB.concat(disciplines, skills, industries)
        const counts = this.sectionCounts();
        const required = this.requiredProgress();
        const filledSections = TB.filter(OPTIONAL_SECTION_KEYS, key => counts[key] > 0).length;
        const canToggle = this.canToggleSections();
        const allOpen = this.allSectionsOpen();

        return [
            /*
            |------------------------------------------------------------------
            | The basics - always visible, always required
            |------------------------------------------------------------------
            */
            {
                style: styles.headerSection,
                fields: {
                    basics_header: {
                        hideVisibility: false,
                        columns: 12,
                        className: 'w-full',
                        component: (
                            <div className="w-full px-3.5 flex flex-row items-center gap-2.5 flex-wrap">
                                <span className="text-sm font-semibold text-dark-gray">
                                    The basics
                                </span>
                                <StatusPill complete={required.isComplete}>
                                    {required.isComplete
                                        ? 'All set'
                                        : `${required.completed} of ${required.total} required`}
                                </StatusPill>
                                {this.props.isEditing && (
                                    <span className="text-xxs text-light-gray4 ml-auto text-right">
                                        These three fields are all you need to save this highlight.
                                    </span>
                                )}
                            </div>
                        )
                    }
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    headline: {
                        type: 'text',
                        label: 'Headline',
                        maxLength: 255,
                        required: true,
                        placeholder: 'Case Study Headline',
                        style: styles.field,
                        columns: 5
                    },
                    year: {
                        label: TB.trans('talent.client_work_form.year_label'),
                        placeholder: TB.trans('talent.client_work_form.year_placeholder'),
                        type: 'select',
                        required: true,
                        options: this.yearOptions(),
                        style: styles.field,
                        columns: 5
                    },
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    description: {
                        label: TB.trans('talent.client_work_form.summary_paragraph_label'),
                        placeholder: TB.trans('talent.client_work_form.summary_paragraph_placeholder'),
                        type: 'editor',
                        required: true,
                        style: styles.field,
                        maxLength: 10000,
                        minLength: MIN_SUMMARY_LENGTH,
                        columns: 10,
                        rows: 10,
                        validation: {
                            rules: {
                                maxLength: 10000
                            },
                            errors: {
                                maxLength: TB.trans('validation.max.string', {
                                    'attribute': 'summary',
                                    'max': 10000
                                }),
                                minCharLength: TB.trans('validation.min.string', {
                                    'attribute': 'summary',
                                    'min': MIN_SUMMARY_LENGTH
                                })
                            }
                        }
                    },
                }
            },

            /*
            |------------------------------------------------------------------
            | Hidden inputs backing the tag / brand pickers
            |------------------------------------------------------------------
            */
            {
                style: { display: 'none' },
                fields: {
                    industries: {
                        type: 'hidden',
                        required: false,
                        style: { display: 'none' }
                    },
                    brands: {
                        type: 'hidden',
                        required: false,
                        style: { display: 'none' }
                    },
                    skills: {
                        type: 'hidden',
                        required: false,
                        style: { display: 'none' }
                    },
                    disciplines: {
                        type: 'hidden',
                        required: false,
                        style: { display: 'none' }
                    },
                }
            },

            /*
            |------------------------------------------------------------------
            | Optional extras - collapsed until asked for
            |------------------------------------------------------------------
            */
            {
                style: styles.headerSection,
                fields: {
                    optional_header: {
                        hideVisibility: !this.props.isEditing && filledSections < 1,
                        columns: 12,
                        className: 'w-full',
                        component: (
                            <div className="w-full px-3.5 mt-6 flex flex-col gap-2">
                                <Divider className="mt-0 mb-1" />
                                <div className="flex flex-row items-center gap-3 flex-wrap">
                                    <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-dark-gray">
                                            Make it stand out
                                        </span>
                                        <span className="text-xxs text-light-gray4 mt-0.5">
                                            {this.props.isEditing
                                                ? `Everything below is optional — add what you have, skip the rest. ${filledSections} of ${OPTIONAL_SECTION_KEYS.length} added.`
                                                : `${filledSections} of ${OPTIONAL_SECTION_KEYS.length} optional sections added.`}
                                        </span>
                                    </div>
                                    {canToggle && (
                                        <Button
                                            type="link"
                                            size="small"
                                            className="ml-auto text-xs p-0 h-auto flex flex-row items-center gap-1.5"
                                            onClick={() => this.setAllSections(!allOpen)}>
                                            <i className={`fa fa-${allOpen ? 'compress' : 'expand'} text-xxs`} />
                                            <span>{allOpen ? 'Collapse all' : 'Expand all'}</span>
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )
                    }
                }
            },

            /* ---------------------------------------------------- Visuals */
            this.collapsibleSection({
                key: 'visuals',
                title: 'Visuals',
                hint: 'A key image or video helps your work get noticed.',
                preview: counts.visuals > 0
                    ? `${counts.visuals} image${counts.visuals === 1 ? '' : 's'} added`
                    : null,
                icon: 'fa fa-image',
                count: counts.visuals,
                countLabel: 'added',
                fields: {
                    image_path: {
                        label: this.props.isEditing ? TB.trans('talent.client_work_form.add_photo_label') : 'Key Visual',
                        type: 'pixie',
                        circular: false,
                        ratio: '16:9',
                        width: 300,
                        required: false,
                        style: styles.field,
                        columns: 12
                    },
                    imgAssets: {
                        label: "Other Key Visuals (optional)",
                        type: 'multi-image',
                        display_label: 'Key Visual',
                        accept: '.jpg,.jpeg,.png,.gif,.mp4,.webm,.mov',
                        maxCount: MAX_IMAGE_ASSETS_COUNT,
                        required: false,
                        style: styles.field,
                        columns: 12
                    },
                }
            }),

            /* ---------------------------------------------------- Brands */
            this.collapsibleSection({
                key: 'brands',
                title: 'Brands worked with',
                hint: `Add up to ${MAX_BRANDS_COUNT} brands involved in this project.`,
                preview: brands?.length
                    ? TB.join(TB.map(TB.slice(brands, 0, 2), b => b.name || b.domain), ', ')
                    + (brands.length > 2 ? ` +${brands.length - 2}` : '')
                    : null,
                icon: 'fa fa-briefcase',
                count: counts.brands,
                countLabel: counts.brands === 1 ? 'brand' : 'brands',
                fields: {
                    brands_display: {
                        required: false,
                        label: 'Brands',
                        className: 'search-brand-input field',
                        component: (
                            <div className={"form-group"} style={styles.field}>
                                <label className="col-form-label flex flex-row items-center gap-7">
                                    <span>
                                        Brands Worked With
                                    </span>
                                    {brands?.length > 0 && (
                                        <span className={TB.mapClass("ml-auto text-xxs italic", {
                                            "opacity-7": brands?.length < MAX_BRANDS_COUNT,
                                            "text-danger font-semibold opacity-1": brands?.length === MAX_BRANDS_COUNT,
                                        })}>
                                            Max ({brands?.length} / {MAX_BRANDS_COUNT})
                                        </span>
                                    )}
                                </label>
                                {brands?.length > 0 && (
                                    <div className="items-stretch flex flex-col mt-5">
                                        <div className="discipline-skills-container">
                                            <FlipMove typeName={null} maintainContainerHeight={true}>
                                                {brands.map((brand, brandIndex) => {
                                                    const brandKey = TB.slugify(`brand-${this.props.itemIndex}-${(brand.id || brand.domain || brand.name)}`)
                                                    return (
                                                        <div key={brandKey}

                                                            className="bg-gray-50 ml-8 mb-8 self-stretch flex flex-row items-center p-1">
                                                            <div id={brandKey}>
                                                                <div className="w-24 self-center">
                                                                    {(brand.logo || brand.icon) ?
                                                                        <img className="w-full max-w-full"
                                                                            src={brand.logo || brand.icon}
                                                                            alt={brand.name} /> : (
                                                                            <PictureOutlined
                                                                                className="w-full max-w-full"
                                                                                style={{ fontSize: 56 }} />
                                                                        )}
                                                                </div>
                                                            </div>
                                                            <InfoTooltip type="white"
                                                                target={brandKey}
                                                                placement="top">
                                                                <div className="flex flex-col gap-1">
                                                                    <p className="p-0 text-left m-0 text-lg">{brand.name || brand.domain}</p>
                                                                    {brand.name && brand.domain && (
                                                                        <p className="p-0 text-left m-0 text-sm text-info whitespace-nowrap">
                                                                            {brand.domain}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </InfoTooltip>
                                                            {this.props.isEditing && !this.props.isDisabled && (
                                                                <div className="self-start ml-2 flex flex-col">
                                                                    <div onClick={e => {

                                                                        let updatedBrands = TB.clone(brands)
                                                                        const newBrandsList = updatedBrands.slice();
                                                                        newBrandsList.splice(brandIndex, 1);
                                                                        this.onChange({
                                                                            ...this.state.model,
                                                                            brands: newBrandsList
                                                                        })
                                                                    }}
                                                                        className="clickable fa fa-times-circle text-danger self-start" />
                                                                </div>
                                                            )}

                                                        </div>
                                                    );
                                                })}
                                            </FlipMove>
                                        </div>
                                        <Divider className="mt-0 mb-4" />
                                    </div>
                                )}
                                {this.props.isEditing && (
                                    <div className="mt-1">
                                        <SearchBrandsInput
                                            isDisabled={this.props.isDisabled || brands?.length >= MAX_BRANDS_COUNT}
                                            placeholder={brands?.length < MAX_BRANDS_COUNT ? "Search for brands" : "Max brands limit reached"}
                                            autoClearSearchValue={true}
                                            shouldReset={true}
                                            value={null}
                                            disabledOptions={TB.map(brands, brand => brand.id || brand.brandId || brand.domain)}
                                            onChange={(brand) => this.onChange({
                                                ...this.state.model,
                                                brands: [...(brands || []), brand]
                                            })} />
                                    </div>
                                )}
                            </div>
                        ),
                        columns: 12
                    }
                }
            }),

            /* ---------------------------------------------------- Tags */
            this.collapsibleSection({
                key: 'tags',
                title: 'Roles, skills & industries',
                hint: 'Tags help Wripple match this work to the right opportunities.',
                preview: allTags?.length
                    ? TB.join(TB.map(TB.slice(allTags, 0, 3), t => t.name), ', ')
                    + (allTags.length > 3 ? ` +${allTags.length - 3}` : '')
                    : null,
                icon: 'fa fa-tags',
                count: counts.tags,
                countLabel: counts.tags === 1 ? 'tag' : 'tags',
                fields: {
                    tag_ids: {
                        component: (
                            <div className={"form-group"} style={styles.field}>
                                <label className="col-form-label flex flex-row items-center gap-7">
                                    <span>
                                        Tags
                                    </span>
                                    {allTags?.length > 0 && (
                                        <span className={TB.mapClass("ml-auto text-xxs italic", {
                                            "opacity-7": allTags?.length < MAX_TAGS_COUNT,
                                            "text-danger font-semibold opacity-1": allTags?.length === MAX_TAGS_COUNT,
                                        })}>
                                            Max ({allTags?.length} / {MAX_TAGS_COUNT})
                                        </span>
                                    )}
                                </label>
                                <div className="discipline-skills-container">
                                    <FlipMove typeName={null} maintainContainerHeight={true}>
                                        {TB.map(allTags, (item, index) => {
                                            const colorMap = {
                                                industries: 'default',
                                                skills: 'geekblue',
                                                disciplines: 'purple'
                                            };

                                            const onPreventMouseDown = (event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            };

                                            return (
                                                <Tag
                                                    key={item.value}
                                                    color={colorMap[item.type]}
                                                    onMouseDown={onPreventMouseDown}
                                                    closable={this.props.isEditing}
                                                    onClose={() => {
                                                        let change = { ...this.state.model };
                                                        change[item.type] = TB.filter([...(change[item.type] || [])], option => parseInt(option?.id) !== parseInt(item.id))
                                                        this.onChange(change)
                                                    }}
                                                    className="mr-3 my-1.5">
                                                    {item.name}
                                                </Tag>
                                            );
                                        })}
                                    </FlipMove>
                                </div>
                            </div>
                        ),
                        columns: 12,
                    },
                    tag_search: {
                        className: 'px-0 mb-2',
                        style: styles.field,
                        component: (
                            <div className="px-3.5">
                                <SearchTagsExpertise
                                    isDisabled={this.props.isDisabled}
                                    isEditable={this.props.isEditing}
                                    value={allTags}
                                    maxCount={MAX_TAGS_COUNT}
                                    onChange={params => {
                                        this.onChange({
                                            ...this.state.model,
                                            ...params
                                        });
                                    }} />
                            </div>
                        ),
                        columns: 12,
                    },
                }
            }),

            /* ------------------------------------------ Documents & links */
            this.collapsibleSection({
                key: 'attachments',
                title: 'Documents & links',
                hint: 'Attach a case study deck, or link out to the live work.',
                preview: counts.attachments > 0
                    ? TB.join(TB.filter([
                        docAssets?.length ? `${docAssets.length} document${docAssets.length === 1 ? '' : 's'}` : null,
                        links?.length ? `${links.length} link${links.length === 1 ? '' : 's'}` : null,
                    ], Boolean), ', ')
                    : null,
                icon: 'fa fa-paperclip',
                count: counts.attachments,
                countLabel: counts.attachments === 1 ? 'item' : 'items',
                fields: {
                    docAssets: {
                        label: "Related Documents (optional)",
                        type: 'multi-doc',
                        display_label: 'Key Visual',
                        accept: '.pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.ppt,.pptx',
                        mime: 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        maxCount: MAX_DOC_ASSETS_COUNT,
                        required: false,
                        style: styles.field,
                        columns: 12
                    },
                    links_section: {
                        columns: 12,
                        component: (
                            <div className={"form-group"} style={styles.field}>
                                <label className="col-form-label flex flex-row items-center gap-7">
                                    <span>
                                        Related Links (optional)
                                    </span>
                                    {links?.length > 0 && (
                                        <span className={TB.mapClass("ml-auto text-xxs italic", {
                                            "opacity-7": links?.length < MAX_LINKS_COUNT,
                                            "text-danger font-semibold opacity-1": links?.length === MAX_LINKS_COUNT,
                                        })}>
                                            Max ({links?.length} / {MAX_LINKS_COUNT})
                                        </span>
                                    )}
                                </label>
                                <div className="flex flex-col gap-3">
                                    {TB.map(links, (link, index) => (
                                        <div key={`link-${index}`} className="flex flex-row items-center gap-1.5">
                                            <i className="fa fa-link text-xxs text-gray-400" />

                                            <a href={link} target="_blank"
                                                className="p-0 btn-link-primary underline text-xs">{link}</a>

                                            <i className="fa fa-times text-danger clickable ml-2" onClick={() => {
                                                return this.onChange({
                                                    ...this.state.model,
                                                    links: TB.filter(links, l => l !== link)
                                                })
                                            }} />
                                        </div>
                                    ))}
                                    {(!links?.length || links?.length < MAX_LINKS_COUNT) && (
                                        <div className="flex flex-row items-center self-stretch gap-2">
                                            <div className="flex-grow relative">
                                                <FormInputComponent
                                                    key={`new-link-input-${links?.length + 1}`}
                                                    fieldName="newLink"
                                                    className="flex-grow"
                                                    type="url"
                                                    placeholder="https://example.com/newLink"
                                                    label={false}
                                                    value={this.state.newLink || ""}
                                                    required={false}
                                                />
                                            </div>
                                            <Button
                                                disabled={this.props.isDisabled || !isValidURLFormat(this.state.model.newLink || "")}
                                                onClick={() => {
                                                    return this.onChange({
                                                        ...this.state.model,
                                                        newLink: "",
                                                        links: [...(links || []), this.state.model.newLink]
                                                    })
                                                }} type="default" className="text-xs flex flex-row gap-1.5"
                                                size="small">
                                                <i className="fa fa-plus ml-1" />
                                                <span>Add Link</span>
                                            </Button>
                                        </div>
                                    )}

                                </div>
                            </div>
                        )
                    },
                }
            }),
        ]
    };

    renderTitle() {
        return null;
    }

    renderFormFields() {
        const { isEditing } = this.props;

        if (isEditing) {
            return (
                <TalentAppCard.Section>
                    {this.renderFormData()}
                </TalentAppCard.Section>
            )
        }

        return (
            <TalentAppCard.Section>
                {this.renderFormattedData()}
            </TalentAppCard.Section>
        )
    }

    renderFormData = () => {
        const { disabled, isEditing } = this.props;
        const isDisabled = disabled || !isEditing;
        const yearOptions = this.yearOptions();

        let {
            industries = [], skills = [], disciplines = [], brands = [],
            imgAssets = [], docAssets = [], links = [],
        } = this.state.model;

        const allTags = TB.concat(disciplines, skills, industries);
        const counts = this.sectionCounts();
        const required = this.requiredProgress();
        const filledSections = TB.filter(OPTIONAL_SECTION_KEYS, key => counts[key] > 0).length;
        const canToggle = this.canToggleSections();
        const allOpen = this.allSectionsOpen();

        return (
            <div>
                {/* The basics */}
                <div className="flex flex-row items-center gap-2.5 flex-wrap mb-4">
                    <span className="text-sm font-semibold text-dark-gray">The basics</span>
                    <StatusPill complete={required.isComplete}>
                        {required.isComplete
                            ? 'All set'
                            : `${required.completed} of ${required.total} required`}
                    </StatusPill>
                    {isEditing && (
                        <span className="text-xxs text-light-gray4 ml-auto text-right">
                            These three fields are all you need to save this highlight.
                        </span>
                    )}
                </div>

                <div className="flex flex-col field sm:flex-row gap-4 [&_.col-form-label]:mb-0 [&_.col-form-label]:pb-0 [&_.invalid-feedback]:relative">
                    <div className="w-full">
                        <FormInputComponent
                            type="antd-input"
                            fieldName="headline"
                            label="Headline"
                            maxLength={255}
                            required
                            value={this.state.model.headline}
                        >
                            <Input
                                placeholder="Case Study Headline"
                                disabled={isDisabled}
                                className="input-default"
                            />
                        </FormInputComponent>
                    </div>
                    <div className="flex flex-row gap-2 form-group field select-field">
                        <div className="w-full max-w-[180px]">
                            <FormInputComponent
                                fieldName="year"
                                label={TB.trans('talent.client_work_form.year_label')}
                                type="select"
                                required
                                options={yearOptions}
                                value={this.state.model.year}
                            />
                        </div>
                    </div>
                </div>

                <div className="field">
                    <FormInputComponent
                        fieldName="description"
                        label={TB.trans('talent.client_work_form.summary_paragraph_label')}
                        type="editor"
                        required
                        maxLength={10000}
                        minLength={MIN_SUMMARY_LENGTH}
                        value={this.state.model.description}
                        validation={{
                            rules: {
                                maxLength: 10000
                            },
                            errors: {
                                maxLength: TB.trans('validation.max.string', {
                                    'attribute': 'summary',
                                    'max': 10000
                                }),
                                minCharLength: TB.trans('validation.min.string', {
                                    'attribute': 'summary',
                                    'min': MIN_SUMMARY_LENGTH
                                })
                            }
                        }}
                    />
                </div>


                {/* Hidden inputs for tag/brand pickers */}
                <div style={{ display: 'none' }}>
                    <FormInputComponent fieldName="industries" type="hidden" required={false} value={industries} />
                    <FormInputComponent fieldName="brands" type="hidden" required={false} value={brands} />
                    <FormInputComponent fieldName="skills" type="hidden" required={false} value={skills} />
                    <FormInputComponent fieldName="disciplines" type="hidden" required={false} value={disciplines} />
                </div>

                {/* Optional extras header */}
                <div className="mt-6 flex flex-col gap-2">
                    <Divider className="mt-0 mb-1" />
                    <div className="flex flex-row items-center gap-3 flex-wrap">
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold text-dark-gray">Make it stand out</span>
                            <span className="text-xxs text-light-gray4 mt-0.5">
                                {isEditing
                                    ? `Everything below is optional — add what you have, skip the rest. ${filledSections} of ${OPTIONAL_SECTION_KEYS.length} added.`
                                    : `${filledSections} of ${OPTIONAL_SECTION_KEYS.length} optional sections added.`}
                            </span>
                        </div>
                        {canToggle && (
                            <Button
                                type="link"
                                size="small"
                                className="ml-auto text-xs p-0 h-auto flex flex-row items-center gap-1.5"
                                onClick={() => this.setAllSections(!allOpen)}>
                                <i className={`fa fa-${allOpen ? 'compress' : 'expand'} text-xxs`} />
                                <span>{allOpen ? 'Collapse all' : 'Expand all'}</span>
                            </Button>
                        )}
                    </div>
                </div>

                {/* Visuals */}
                <CollapsibleSectionHeader
                    sectionKey="visuals"
                    title="Visuals"
                    hint="A key image or video helps your work get noticed."
                    preview={counts.visuals > 0 ? `${counts.visuals} image${counts.visuals === 1 ? '' : 's'} added` : null}
                    icon="fa fa-image"
                    isOpen={this.isSectionOpen('visuals')}
                    count={counts.visuals}
                    countLabel="added"
                    canToggle={canToggle}
                    onToggle={() => this.toggleSection('visuals')}
                />
                {this.isSectionOpen('visuals') && (
                    <div className="pt-2">
                        <FormInputComponent
                            fieldName="image_path"
                            label={isEditing ? TB.trans('talent.client_work_form.add_photo_label') : 'Key Visual'}
                            type="pixie"
                            circular={false}
                            ratio="16:9"
                            width={300}
                            required={false}
                            value={this.state.model.image_path}
                        />
                        <FormInputComponent
                            fieldName="imgAssets"
                            label="Other Key Visuals (optional)"
                            type="multi-image"
                            display_label="Key Visual"
                            accept=".jpg,.jpeg,.png,.gif,.mp4,.webm,.mov"
                            maxCount={MAX_IMAGE_ASSETS_COUNT}
                            required={false}
                            value={imgAssets}
                        />
                    </div>
                )}

                {/* Brands */}
                <CollapsibleSectionHeader
                    sectionKey="brands"
                    title="Brands worked with"
                    hint={`Add up to ${MAX_BRANDS_COUNT} brands involved in this project.`}
                    preview={brands?.length
                        ? TB.join(TB.map(TB.slice(brands, 0, 2), b => b.name || b.domain), ', ')
                        + (brands.length > 2 ? ` +${brands.length - 2}` : '')
                        : null}
                    icon="fa fa-briefcase"
                    isOpen={this.isSectionOpen('brands')}
                    count={counts.brands}
                    countLabel={counts.brands === 1 ? 'brand' : 'brands'}
                    canToggle={canToggle}
                    onToggle={() => this.toggleSection('brands')}
                />
                {this.isSectionOpen('brands') && (
                    <div className="pt-2">
                        <div className={"form-group"}>
                            <label className="col-form-label flex flex-row items-center gap-7">
                                <span>Brands Worked With</span>
                                {brands?.length > 0 && (
                                    <span className={TB.mapClass("ml-auto text-xxs italic", {
                                        "opacity-7": brands?.length < MAX_BRANDS_COUNT,
                                        "text-danger font-semibold opacity-1": brands?.length === MAX_BRANDS_COUNT,
                                    })}>
                                        Max ({brands?.length} / {MAX_BRANDS_COUNT})
                                    </span>
                                )}
                            </label>
                            {brands?.length > 0 && (
                                <div className="items-stretch flex flex-col mt-5">
                                    <div className="discipline-skills-container">
                                        <FlipMove typeName={null} maintainContainerHeight={true}>
                                            {brands.map((brand, brandIndex) => {
                                                const brandKey = TB.slugify(`brand-${this.props.itemIndex}-${(brand.id || brand.domain || brand.name)}`)
                                                return (
                                                    <div key={brandKey} className="bg-gray-50 ml-8 mb-8 self-stretch flex flex-row items-center p-1">
                                                        <div id={brandKey}>
                                                            <div className="w-24 self-center">
                                                                {(brand.logo || brand.icon) ?
                                                                    <img className="w-full max-w-full" src={brand.logo || brand.icon} alt={brand.name} /> : (
                                                                        <PictureOutlined className="w-full max-w-full" style={{ fontSize: 56 }} />
                                                                    )}
                                                            </div>
                                                        </div>
                                                        <InfoTooltip type="white" target={brandKey} placement="top">
                                                            <div className="flex flex-col gap-1">
                                                                <p className="p-0 text-left m-0 text-lg">{brand.name || brand.domain}</p>
                                                                {brand.name && brand.domain && (
                                                                    <p className="p-0 text-left m-0 text-sm text-info whitespace-nowrap">{brand.domain}</p>
                                                                )}
                                                            </div>
                                                        </InfoTooltip>
                                                        {isEditing && !this.props.isDisabled && (
                                                            <div className="self-start ml-2 flex flex-col">
                                                                <div onClick={() => {
                                                                    let updatedBrands = TB.clone(brands)
                                                                    const newBrandsList = updatedBrands.slice();
                                                                    newBrandsList.splice(brandIndex, 1);
                                                                    this.onChange({ ...this.state.model, brands: newBrandsList })
                                                                }} className="clickable fa fa-times-circle text-danger self-start" />
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </FlipMove>
                                    </div>
                                    <Divider className="mt-0 mb-4" />
                                </div>
                            )}
                            {isEditing && (
                                <div className="mt-1">
                                    <SearchBrandsInput
                                        isDisabled={this.props.isDisabled || brands?.length >= MAX_BRANDS_COUNT}
                                        placeholder={brands?.length < MAX_BRANDS_COUNT ? "Search for brands" : "Max brands limit reached"}
                                        autoClearSearchValue={true}
                                        shouldReset={true}
                                        value={null}
                                        disabledOptions={TB.map(brands, brand => brand.id || brand.brandId || brand.domain)}
                                        onChange={(brand) => this.onChange({ ...this.state.model, brands: [...(brands || []), brand] })}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Tags */}
                <CollapsibleSectionHeader
                    sectionKey="tags"
                    title="Roles, skills & industries"
                    hint="Tags help Wripple match this work to the right opportunities."
                    preview={allTags?.length
                        ? TB.join(TB.map(TB.slice(allTags, 0, 3), t => t.name), ', ')
                        + (allTags.length > 3 ? ` +${allTags.length - 3}` : '')
                        : null}
                    icon="fa fa-tags"
                    isOpen={this.isSectionOpen('tags')}
                    count={counts.tags}
                    countLabel={counts.tags === 1 ? 'tag' : 'tags'}
                    canToggle={canToggle}
                    onToggle={() => this.toggleSection('tags')}
                />
                {this.isSectionOpen('tags') && (
                    <div className="pt-2">
                        <div className={"form-group"}>
                            <label className="col-form-label flex flex-row items-center gap-7">
                                <span>Tags</span>
                                {allTags?.length > 0 && (
                                    <span className={TB.mapClass("ml-auto text-xxs italic", {
                                        "opacity-7": allTags?.length < MAX_TAGS_COUNT,
                                        "text-danger font-semibold opacity-1": allTags?.length === MAX_TAGS_COUNT,
                                    })}>
                                        Max ({allTags?.length} / {MAX_TAGS_COUNT})
                                    </span>
                                )}
                            </label>
                            <div className="discipline-skills-container">
                                <FlipMove typeName={null} maintainContainerHeight={true}>
                                    {TB.map(allTags, (item) => {
                                        const colorMap = { industries: 'default', skills: 'geekblue', disciplines: 'purple' };
                                        return (
                                            <Tag
                                                key={item.value}
                                                color={colorMap[item.type]}
                                                closable={isEditing}
                                                onClose={() => {
                                                    let change = { ...this.state.model };
                                                    change[item.type] = TB.filter([...(change[item.type] || [])], option => parseInt(option?.id) !== parseInt(item.id))
                                                    this.onChange(change)
                                                }}
                                                className="mr-3 my-1.5">
                                                {item.name}
                                            </Tag>
                                        );
                                    })}
                                </FlipMove>
                            </div>
                        </div>
                        {isEditing && (
                            <div className="px-3.5">
                                <SearchTagsExpertise
                                    isDisabled={this.props.isDisabled}
                                    isEditable={isEditing}
                                    value={allTags}
                                    maxCount={MAX_TAGS_COUNT}
                                    onChange={params => {
                                        this.onChange({ ...this.state.model, ...params });
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Documents & links */}
                <CollapsibleSectionHeader
                    sectionKey="attachments"
                    title="Documents & links"
                    hint="Attach a case study deck, or link out to the live work."
                    preview={counts.attachments > 0
                        ? TB.join(TB.filter([
                            docAssets?.length ? `${docAssets.length} document${docAssets.length === 1 ? '' : 's'}` : null,
                            links?.length ? `${links.length} link${links.length === 1 ? '' : 's'}` : null,
                        ], Boolean), ', ')
                        : null}
                    icon="fa fa-paperclip"
                    isOpen={this.isSectionOpen('attachments')}
                    count={counts.attachments}
                    countLabel={counts.attachments === 1 ? 'item' : 'items'}
                    canToggle={canToggle}
                    onToggle={() => this.toggleSection('attachments')}
                />
                {this.isSectionOpen('attachments') && (
                    <div className="pt-2">
                        <FormInputComponent
                            fieldName="docAssets"
                            label="Related Documents (optional)"
                            type="multi-doc"
                            display_label="Key Visual"
                            accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.ppt,.pptx"
                            mime="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                            maxCount={MAX_DOC_ASSETS_COUNT}
                            required={false}
                            value={docAssets}
                        />

                        <div className={"form-group"}>
                            <label className="col-form-label flex flex-row items-center gap-7">
                                <span>Related Links (optional)</span>
                                {links?.length > 0 && (
                                    <span className={TB.mapClass("ml-auto text-xxs italic", {
                                        "opacity-7": links?.length < MAX_LINKS_COUNT,
                                        "text-danger font-semibold opacity-1": links?.length === MAX_LINKS_COUNT,
                                    })}>
                                        Max ({links?.length} / {MAX_LINKS_COUNT})
                                    </span>
                                )}
                            </label>
                            <div className="flex flex-col gap-3">
                                {TB.map(links, (link, index) => (
                                    <div key={`link-${index}`} className="flex flex-row items-center gap-1.5">
                                        <i className="fa fa-link text-xxs text-gray-400" />
                                        <a href={link} target="_blank" className="p-0 btn-link-primary underline text-xs">{link}</a>
                                        <i className="fa fa-times text-danger clickable ml-2" onClick={() => {
                                            return this.onChange({ ...this.state.model, links: TB.filter(links, l => l !== link) })
                                        }} />
                                    </div>
                                ))}
                                {(!links?.length || links?.length < MAX_LINKS_COUNT) && (
                                    <div className="flex flex-row items-center self-stretch gap-2">
                                        <div className="flex-grow relative">
                                            <FormInputComponent
                                                key={`new-link-input-${links?.length + 1}`}
                                                fieldName="newLink"
                                                type="antd-input"
                                                label={false}
                                                value={this.state.model.newLink || ""}
                                                required={false}
                                                validation={{
                                                    rules: {
                                                        matchRegexp: /^(http|https):\/\//,
                                                        isUrl: true,
                                                    },
                                                    errors: {
                                                        matchRegexp: 'Must start with http(s)://',
                                                        isUrl: TB.trans('validation.active_url', { 'attribute': 'link' }),
                                                    }
                                                }}
                                            >
                                                <Input
                                                    placeholder="https://example.com/newLink"
                                                    disabled={isDisabled}
                                                    className="input-default"
                                                />
                                            </FormInputComponent>
                                        </div>
                                        <Button
                                            disabled={this.props.isDisabled || !isValidURLFormat(this.state.model.newLink || "")}
                                            onClick={() => {
                                                return this.onChange({
                                                    ...this.state.model,
                                                    newLink: "",
                                                    links: [...(links || []), this.state.model.newLink]
                                                })
                                            }} type="default" className="text-xs flex flex-row gap-1.5"
                                            size="small">
                                            <i className="fa fa-plus ml-1" />
                                            <span>Add Link</span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    renderFormattedData() {
        const { headline, year, description, image_path, brands = [], industries = [], skills = [], disciplines = [] } = this.state.model;
        const allTags = TB.concat(disciplines, skills, industries);

        return (
            <div className="flex flex-col sm:flex-row items-start gap-4">
                {image_path && (
                    <div className="shrink-0">
                        <img
                            src={image_path}
                            alt={`${headline} visual`}
                            className="h-[64px] w-[100px] rounded object-cover"
                        />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-x-4 gap-y-1 mb-1">
                        <p className="text-sm font-semibold m-0 truncate">{headline || '—'}</p>
                        {year && (
                            <p className="text-xs text-gray-500 m-0">{year}</p>
                        )}
                    </div>
                    {description && (
                        <p className="text-xs text-gray-600 m-0 line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: description }} />
                    )}
                    {(brands?.length > 0 || allTags?.length > 0) && (
                        <div className="flex flex-row flex-wrap gap-1.5 mt-2">
                            {TB.map(brands, (brand, i) => (
                                <span key={`brand-${i}`} className="inline-flex items-center gap-1 text-xxs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">
                                    {(brand.logo || brand.icon) && (
                                        <img src={brand.logo || brand.icon} alt="" className="h-3 w-3 rounded-full object-contain" />
                                    )}
                                    {brand.name || brand.domain}
                                </span>
                            ))}
                            {TB.map(allTags, (tag, i) => {
                                const colorMap = { industries: 'default', skills: 'geekblue', disciplines: 'purple' };
                                return <Tag key={tag.value} color={colorMap[tag.type]} className="m-0 text-xxs">{tag.name}</Tag>
                            })}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    renderFormFooterInfo() {
        return <div />
    }

    renderEditFormButton() {
        return (
            <button
                className="btn btn-primary px-6"
                disabled={this.props.disabled}
                type="button" onClick={() => this.props.onEditing(true)}
            >
                Edit Work Sample
            </button>
        );
    }

    renderProfilePreview() {
        return (
            <TalentAppCard.Section>
                <ProfileKeyClientRow project={this.getModelFromProps()} />
            </TalentAppCard.Section>
        )
    }
}

const SearchTagsExpertise = ({ onChange, value, isDisabled, isEditable, maxCount = 12, ...props }) => {
    const [searchValue, setSearchValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const tag_ids = TB.map(value || [], 'value');
    const {
        data,
        isInitialLoading: isLoading,
        isFetching,
        error
    } = useAPI(['searchTagsExpertise', { search: searchValue }], {
        select: items => props.filterByType ? { [props.filterByType]: items[props.filterByType] } : items,
        enabled: String(searchValue).length > 0,
        initialData: props.initValues || [],
    })

    const searchOptions = useMemo(() => {
        if (isLoading || !data) {
            return;
        }

        const industryOptions = TB.map(data.industries || [], ind => {
            return {
                value: `industry-${parseInt(ind.id)}`,
                type: 'industries',
                label: (
                    <div className="flex flex-row justify-between gap-3 mr-3">
                        <p className="flex-grow">{ind.name}</p>
                        <Tag color='default' closable={false}>Industry</Tag>
                    </div>
                ),
                name: ind.name,
                id: parseInt(ind.id)
            }
        }
        );

        const disciplineOptions = TB.map(data.disciplines || [], dis => ({
            value: `discipline-${parseInt(dis.id)}`,
            type: 'disciplines',
            label: (
                <div className="flex flex-row justify-between gap-3 mr-3">
                    <p className="flex-grow">{dis.name}</p>
                    <Tag color='purple' closable={false}>Wripple Role</Tag>
                </div>
            ),
            name: dis.name,
            id: parseInt(dis.id)
        }));

        let skillOptions = TB.map(data.skills || [], skill => ({
            value: `skill-${parseInt(skill.id)}`,
            type: 'skills',
            label: (
                <div className="flex flex-row justify-between gap-3 mr-3">
                    <p className="flex-grow">{skill.name}</p>
                    <Tag color='geekblue' closable={false}>Skill</Tag>
                </div>
            ),
            name: skill.name,
            id: parseInt(skill.id)
        }));

        return TB.concat(disciplineOptions, skillOptions, industryOptions)
    }, [data, isFetching]);

    return (
        <div>
            <Select
                // mode="multiple"
                // showArrow
                size="small"
                disabled={isDisabled || value?.length >= maxCount}
                placeholder={value?.length < maxCount ? (props.placeholder || "Search tags for roles, skills, industries") : "Max tags limit reached"}
                open={isOpen}
                showArrow={value?.length < maxCount}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setIsOpen(false)}
                searchValue={searchValue || ''}
                onSearch={value => setSearchValue(value)}
                tagRender={tagRender}
                showSearch={true}
                filterOption={(input, option) => tag_ids.includes(option.value) ? false : (
                    (option?.name ?? '')?.toLowerCase().includes(TB.trimStart(input?.toLowerCase()))) ||
                    TB.trimStart(input?.toLowerCase()).includes((option?.name ?? '')?.toLowerCase()
                    )}
                filterSort={(optionA, optionB) => {
                    const sortValue = value => {
                        if (value.includes('discipline')) {
                            return 0
                        }
                        if (value.includes('skill')) {
                            return 10
                        }
                        if (value.includes('industry')) {
                            return 20
                        }
                    }
                    let sort = sortValue(optionA.value) - sortValue(optionB.value);
                    return sort + (optionA?.name ?? '').toLowerCase().localeCompare((optionB?.name ?? '').toLowerCase())
                }}
                optionFilterProp="children"
                optionLabelProp={searchOptions?.length > 0 ? 'name' : undefined}
                value={null}
                notFoundContent={isFetching ? (
                    <div className="flex flex-col items-center justify-center my-8 h-20 gap-2">
                        <LoadingSpinner size={'lg'} />

                        <p className="text-sm opacity-6 text-center">Loading results...</p>
                    </div>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={searchValue?.length ? "No tags found." : 'Start typing to search'} />
                )}
                loading={isFetching}
                autoClearSearchValue={false}
                onSelect={(selectValue, option) => {
                    let options = TB.filter(value || [], ['type', option.type]);
                    let update = { [option.type]: [...options, TB.pick(option, ['id', 'name', 'value', 'type'])] }
                    console.log('onSelectExpertise', { options, update });
                    onChange(update)
                }}
                style={{ width: '100%' }}
                options={(searchValue?.length && searchOptions?.length) ? searchOptions : []}

            />
        </div>
    )
}

export {
    SearchTagsExpertise
}
const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
    headerSection: {
        flexDirection: 'row',
        alignItems: 'stretch',
        width: '100%',
    },
    collapsibleSection: {
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
    },
    sectionBodyOpen: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        width: '100%',
    },
    sectionBodyClosed: {
        display: 'none',
    },
};
